import { EditOutlined, SwapOutlined } from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { usePermission } from "@/shared/hooks/usePermission";
import { useDepartmentsExplorer, useDepartmentsList } from "../hooks/useDepartments";
import type { DepartmentListItem } from "../types";
import { departmentAncestorNames } from "../utils/explorerPath";
import type { DepartmentTab } from "../utils/tabs";

interface DepartmentsExplorerTableProps {
	parentId?: string;
	q?: string;
	isActive?: boolean;
	onEdit?: (department: DepartmentListItem) => void;
	onMove?: (department: DepartmentListItem) => void;
	/**
	 * Qator havolasiga qo'shiladigan `?tab=`. "Bo'linmalar" tabi
	 * `{ tab: "bolinmalar" }` beradi — Finder drill-down: farzand profili O'Z
	 * "Bo'linmalar" tabi bilan ochiladi. Berilmasa (ildiz sahifasi) profil
	 * standart ("Umumiy") tab bilan ochiladi.
	 */
	rowSearch?: { tab?: DepartmentTab };
}

function buildColumns(
	t: TFunction<"departments">,
	showActions: boolean,
	nameById: Map<string, string> | null,
	onEdit?: (department: DepartmentListItem) => void,
	onMove?: (department: DepartmentListItem) => void
): ColumnsType<DepartmentListItem> {
	return [
		{
			title: t("explorer.columns.name"),
			dataIndex: "name",
			key: "name",
			render: (_, dept) => {
				// ⚠️ Joriy bo'limning O'ZI yo'lga kiritilmaydi
				// (`departmentAncestorNames`). `nameById` yo'q (ildiz emas yoki
				// `q`/`isActive=false` yo'q) — subtitle umuman chizilmaydi.
				const yolNomlari = nameById ? departmentAncestorNames(dept.path, nameById) : [];
				return (
					<div>
						<div className="font-medium">{dept.name}</div>
						{yolNomlari.length > 0 && (
							<div className="text-xs text-gray-400">{yolNomlari.join(" / ")}</div>
						)}
						{dept.childrenCount > 0 && (
							<div className="text-xs text-gray-400">
								{t("explorer.childrenSubtitle", { count: dept.childrenCount })}
							</div>
						)}
					</div>
				);
			},
		},
		{
			title: t("explorer.columns.grade"),
			key: "grade",
			render: (_, dept) => <Tag color="purple">{`${dept.gradeLevel} · ${dept.gradeName}`}</Tag>,
		},
		{
			title: t("explorer.columns.employees"),
			dataIndex: "employeesCount",
			key: "employeesCount",
			align: "center",
		},
		{
			title: t("explorer.columns.zones"),
			dataIndex: "zonesCount",
			key: "zonesCount",
			align: "center",
		},
		{
			title: t("explorer.columns.children"),
			dataIndex: "childrenCount",
			key: "childrenCount",
			align: "center",
		},
		// ⚠️⚠️ Ustunning O'ZI qo'shilmaydi — `PermissionGate` bolalarini
		// yashirsa ham bo'sh ustun (sarlavha + bo'sh katak) qolar edi (review
		// Important 6). Har tugma o'z handler'i borligiga ham qarab
		// gatelanadi: ruxsat bor, lekin `onEdit`/`onMove` uzatilmasa tugma
		// bosilishi JIMGINA hech narsa qilmasdi.
		...(showActions
			? [
					{
						title: t("explorer.columns.actions"),
						key: "actions",
						align: "right" as const,
						render: (_: unknown, dept: DepartmentListItem) => (
							<Space size={2}>
								{onEdit ? (
									<Button
										type="text"
										icon={<EditOutlined />}
										title={t("page.edit")}
										onClick={(event) => {
											// ⚠️ Usiz tugma bosilishi qatorning `onClick` iga
											// ko'tarilib, foydalanuvchi forma o'rniga bo'lim
											// profiliga tushib ketardi.
											event.stopPropagation();
											onEdit(dept);
										}}
									/>
								) : null}
								{onMove ? (
									<Button
										type="text"
										icon={<SwapOutlined />}
										title={t("page.move")}
										onClick={(event) => {
											event.stopPropagation();
											onMove(dept);
										}}
									/>
								) : null}
							</Space>
						),
					},
				]
			: []),
	];
}

/**
 * Finder uslubidagi drill-down jadvali — ILDIZ sahifasida (`DepartmentsPage`)
 * VA bo'lim profilining "Bo'linmalar" tabida (4-reja) QAYTA ishlatiladi.
 * Qator bosilishi HAR DOIM to'liq profilga o'tadi — daraxt YO'Q, drill-down
 * shu jadval + breadcrumb orqali davom etadi.
 */
export function DepartmentsExplorerTable({
	parentId,
	q,
	isActive,
	onEdit,
	onMove,
	rowSearch,
}: DepartmentsExplorerTableProps) {
	const { t } = useTranslation("departments");
	const navigate = useNavigate();
	const { can } = usePermission();
	const query = useDepartmentsExplorer({ parentId, q, isActive });
	// ⚠️ Ruxsat bor-yo'qligidan tashqari, HECH BO'LMASA bitta handler
	// uzatilgan bo'lishi shart — aks holda ustun bo'sh tugmalarga bo'lardi.
	const showActions = can("departments", "update") && Boolean(onEdit || onMove);

	// ⚠️⚠️ Tekis qidiruvda (spec §9) — ILDIZ ko'rinishida (`parentId` yo'q)
	// `q` berilgan YOKI `isActive=false` bo'lsa, backend butun ko'rinadigan
	// daraxt bo'yicha TEKIS natija qaytaradi. Foydalanuvchi qatorning QAYERDA
	// turganini bilishi uchun yo'l subtitle kerak. `parentId` berilgan
	// (drill-down) ko'rinishda o'zgarish YO'Q — u holatda daraxt allaqachon
	// breadcrumb orqali ko'rinadi.
	const showPath = parentId === undefined && (Boolean(q) || isActive === false);
	// `enabled` — subtitle kerak bo'lmaganda `GET /departments` SO'RALMAYDI.
	const { data: allDepartments } = useDepartmentsList({ enabled: showPath });
	const nameById = useMemo(
		() => (showPath && allDepartments ? new Map(allDepartments.map((d) => [d.id, d.name])) : null),
		[showPath, allDepartments]
	);

	const columns = buildColumns(t, showActions, nameById, onEdit, onMove);

	// ⚠️⚠️ **Xato ≠ bo'sh natija** (C2 Task 7b). `usePaginatedQuery` `items`ni
	// xato holatida ham `[]` qaytaradi — Alert'siz `DataTable` "hech qanday
	// bo'lim yo'q" degan jadvalni chizib, foydalanuvchiga so'rov 500 bilan
	// yiqilganini yashirardi.
	if (query.isError) {
		return (
			<Alert
				type="error"
				showIcon
				title={t("page.loadError")}
				description={t("page.loadErrorDescription")}
			/>
		);
	}

	return (
		<DataTable<DepartmentListItem>
			columns={columns}
			query={query}
			onRow={(record) => ({
				// ⚠️ `search` ANIQ beriladi (`?? {}`): TanStack'ning "search
				// berilmagan" standartiga tayanilmaydi — ildiz sahifasida `?tab=`
				// yo'q, tabda esa `rowSearch` bo'yicha AYNAN shu.
				onClick: () =>
					navigate({
						to: "/departments/$id",
						params: { id: record.id },
						search: rowSearch ?? {},
					}),
				className: "cursor-pointer",
			})}
		/>
	);
}
